/**
 * alpha-webm-recorder.js — transparent (alpha) WebM via MediaRecorder.
 *
 * WHY: our FFmpeg-WASM libvpx (VP8/VP9) OOBs on real content, so the
 * `-c:v libvpx -pix_fmt yuva420p` alpha-WebM path (video-bg-remover default,
 * animated-avatar) is broken. WebCodecs VideoEncoder CANNOT encode alpha
 * (measured: isConfigSupported({alpha:'keep'})=false). The ONLY working
 * client-side alpha-WebM encoder is MediaRecorder — MEASURED 2026-07-02:
 *   - Chromium: alpha round-trips (transparent pixels survive) for vp8/vp9.
 *   - Firefox: alpha is FLATTENED (lost) — so this is Chromium-only; callers
 *     keep the working non-libvpx alpha formats (APNG / animated-WebP / ProRes)
 *     for other browsers.
 *   - MediaRecorder timestamps by WALL-CLOCK; pushing frames faster than
 *     realtime compresses the duration (playback too fast). So correct
 *     duration REQUIRES realtime pacing (one frame per 1000/fps ms). That
 *     makes encode take ~clip-length — acceptable for these tools (short
 *     clips; bg-remover's per-frame ML inference already dominates).
 *
 * API — window.AlphaWebMRecorder:
 *   await isSupported({fps})  → { ok, codec } | { ok:false, reason }
 *       Micro round-trip probe: encodes 3 alpha frames, decodes them back and
 *       checks a transparent pixel survived — so a green result is evidence,
 *       not a capability string.
 *       ⚠️ A NEGATIVE is only cached when the browser actually answered
 *       ('alpha-flattened', 'no-mediarecorder', 'no-webm-mime',
 *       'no-requestframe' — Firefox). A probe that
 *       merely failed to come off is retried once and, if it still says
 *       nothing, is not cached at all — see the INCONCLUSIVE note below for the
 *       measurement that forced this (2026-09-01).
 *   const s = createSession({ width, height, fps })
 *   s.addFrame(canvasOrBitmap)   // buffered (ImageBitmap); cheap
 *   await s.finalize(onProgress) // paces frames → MediaRecorder → Blob(webm, alpha); blob.frameCount
 *       — the caller's retime cuts the stream to that many frames (-frames:v)
 *   s.cleanup()
 *
 * Memory: buffers N ImageBitmaps (GPU-backed, light) — for the short clips
 * these tools produce. Callers that generate huge frame counts should cap.
 */
(function (root) {
    'use strict';
    if (root.AlphaWebMRecorder) return;

    var VP_CANDIDATES = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8'];

    function pickMime() {
        if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return null;
        for (var i = 0; i < VP_CANDIDATES.length; i++) {
            if (MediaRecorder.isTypeSupported(VP_CANDIDATES[i])) return VP_CANDIDATES[i];
        }
        return null;
    }

    var _probe = null;

    /* A verdict the BROWSER gave, versus a measurement that did not come off.
       The distinction decides whether the answer may be cached forever: an
       engine that flattens alpha will keep flattening it, but a probe that ran
       while the page was still loading 30 MB of scripts tells us nothing about
       the engine — and caching THAT is how a capability disappears for the rest
       of the session.

       Measured 2026-09-01, real Chrome 152, headed and headless alike: called
       during page load the probe records a 110-byte WebM with no frames in it
       and reports `probe-unplayable`; the identical call 1.5 s later records a
       706-byte clip and reports alpha preserved. Every consumer then took the
       worse path for the rest of the session on the strength of one unlucky
       moment. */
    var INCONCLUSIVE = { 'empty-probe': 1, 'probe-unplayable': 1 };
    function inconclusive(r) {
        if (!r || r.ok) return false;
        return !!INCONCLUSIVE[r.reason] || String(r.reason || '').indexOf('probe-throw') === 0;
    }

    function isSupported(opts) {
        if (_probe) return _probe;
        _probe = (async function () {
            var first = await runProbe();
            if (!inconclusive(first)) return first;
            // One retry, after the frame — long enough for the main thread to
            // have handed the encoder a slot.
            await new Promise(function (r) { requestAnimationFrame(function () { setTimeout(r, 250); }); });
            var second = await runProbe();
            if (inconclusive(second)) _probe = null;   // still no answer — ask again next time
            return second;
        })();
        return _probe;
    }

    function runProbe() {
        return (async function () {
            try {
                if (typeof MediaRecorder === 'undefined' || typeof HTMLCanvasElement === 'undefined') return { ok: false, reason: 'no-mediarecorder' };
                var mime = pickMime();
                if (!mime) return { ok: false, reason: 'no-webm-mime' };
                // Firefox: CanvasCaptureMediaStreamTrack has no requestFrame(), so
                // the frames cannot be driven at all — the session below would
                // throw on the first frame. That is the browser's answer, not a
                // measurement that failed, so it is reported as its own verdict
                // (measured 2026-09-02: the probe threw "track.requestFrame is not
                // a function", which the retry logic read as inconclusive and left
                // the transparent-WebM destinations OPEN in a browser that cannot
                // produce them).
                try {
                    var probeTrack = document.createElement('canvas').captureStream(0).getVideoTracks()[0];
                    var canDrive = probeTrack && typeof probeTrack.requestFrame === 'function';
                    try { probeTrack && probeTrack.stop(); } catch (_) {}
                    if (!canDrive) return { ok: false, reason: 'no-requestframe' };
                } catch (_) { return { ok: false, reason: 'no-requestframe' }; }
                // Micro round-trip: 2 frames, left opaque / right cleared. If the
                // right sample decodes with alpha<250, transparency survives here
                // (Chromium yes, Firefox no — measured).
                var W = 64, H = 48;
                var c = document.createElement('canvas'); c.width = W; c.height = H;
                var ctx = c.getContext('2d', { alpha: true });
                // A cold first recorder of the tab drops its first frames — the
                // probe's three frames all of them (110-byte WebM, measured
                // 09.10.2026: 3 of 3 fresh tabs answered probe-unplayable).
                if (root.__mediaRecorderWarm) await root.__mediaRecorderWarm({ width: W, height: H, mimeType: mime, videoBitsPerSecond: 400000 });
                var stream = c.captureStream(0);
                var track = stream.getVideoTracks()[0];
                var chunks = [];
                var rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 400000 });
                rec.ondataavailable = function (e) { if (e.data && e.data.size) chunks.push(e.data); };
                var stopped = new Promise(function (r) { rec.onstop = r; });
                // Pushing frames before the recorder has actually started is the
                // other half of the empty-blob failure above.
                var started = new Promise(function (r) { rec.onstart = r; setTimeout(r, 300); });
                rec.start();
                await started;
                for (var i = 0; i < 3; i++) {
                    ctx.clearRect(0, 0, W, H);
                    ctx.fillStyle = 'rgba(255,0,0,1)'; ctx.fillRect(0, 0, W / 2, H);
                    track.requestFrame();
                    await new Promise(function (r) { setTimeout(r, 20); });
                }
                await new Promise(function (r) { setTimeout(r, 40); });
                rec.stop(); await stopped;
                var blob = new Blob(chunks, { type: 'video/webm' });
                if (!blob.size) return { ok: false, reason: 'empty-probe' };
                var v = document.createElement('video'); v.muted = true; v.src = URL.createObjectURL(blob); try { v.load(); } catch (_) { }
                try { await new Promise(function (res, rej) { v.onloadeddata = res; v.onerror = function () { rej(new Error('load')); }; setTimeout(function () { rej(new Error('t/o')); }, 6000); }); }
                catch (e) { return { ok: false, reason: 'probe-unplayable' }; }
                /* Wait for actual frame PRESENTATION, not bare seeked: an
                   undrawn (raced) canvas keeps alpha=0 everywhere, which this
                   probe reads as "alpha preserved" - a false OK on browsers
                   that flatten alpha. */
                try {
                    if (window.__seekPresented) { await window.__seekPresented(v, 0.01, 800); }
                    else { v.currentTime = 0.01; await new Promise(function (r) { v.onseeked = r; setTimeout(r, 500); }); }
                } catch (_) {}
                var oc = document.createElement('canvas'); oc.width = W; oc.height = H;
                var octx = oc.getContext('2d', { alpha: true }); octx.clearRect(0, 0, W, H); octx.drawImage(v, 0, 0, W, H);
                var rightAlpha = octx.getImageData(W * 0.9, H / 2, 1, 1).data[3];
                try { URL.revokeObjectURL(v.src); } catch (_) {}
                if (rightAlpha >= 250) return { ok: false, reason: 'alpha-flattened' };  // Firefox
                return { ok: true, codec: mime };
            } catch (e) { return { ok: false, reason: 'probe-throw:' + ((e && e.message) || '').slice(0, 40) }; }
        })();
    }

    function createSession(o) {
        var W = o.width, H = o.height, fps = o.fps || 30;
        var frames = [];   // ImageBitmap[]
        var pending = [];  // promises for in-flight createImageBitmap

        function addFrame(src) {
            // Snapshot the frame now (source canvas is reused by the caller).
            // createImageBitmap keeps a GPU-backed copy — light + preserves alpha.
            var p = createImageBitmap(src).then(function (bmp) { frames.push(bmp); });
            pending.push(p);
            return p;
        }

        async function finalize(onProgress) {
            await Promise.all(pending);
            if (!frames.length) throw new Error('alpha-recorder: no frames');
            var mime = pickMime();
            if (!mime) throw new Error('alpha-recorder: no webm mime');
            var c = document.createElement('canvas'); c.width = W; c.height = H;
            var ctx = c.getContext('2d', { alpha: true });
            var bps = Math.round(Math.max(1, W * H * fps * 0.12));
            // Frames are retimed by COUNT afterwards, so the frames a cold
            // first recorder drops are lost, not frozen: 90-frame sticker →
            // 11–39 frames or a 110-byte file with none (measured 09.10.2026;
            // warmed at this size: 88/90).
            if (root.__mediaRecorderWarm) await root.__mediaRecorderWarm({ width: W, height: H, mimeType: mime, videoBitsPerSecond: bps });
            var stream = c.captureStream(0);
            var track = stream.getVideoTracks()[0];
            var chunks = [];
            var rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: bps });
            rec.ondataavailable = function (e) { if (e.data && e.data.size) chunks.push(e.data); };
            var stopped = new Promise(function (r) { rec.onstop = r; });
            var frameMs = 1000 / fps;
            rec.start();
            // Pacing: faster than realtime and the recorder drops frames (90 →
            // 11). The duration itself comes from the caller's retime by frame
            // COUNT. The encoder of a fresh recorder is also slow over its first
            // frames, and Chromium keeps at most ~10 in flight: realtime from
            // the start lost a burst from frame 11 on (2–16 frames, 3 of 6
            // runs); 100 ms per frame over the first 20 — 0 of 6 (09.10.2026).
            for (var i = 0; i < frames.length; i++) {
                ctx.clearRect(0, 0, W, H);
                ctx.drawImage(frames[i], 0, 0, W, H);
                track.requestFrame();
                if (onProgress) { try { onProgress(i / frames.length); } catch (_) {} }
                await new Promise(function (r) { setTimeout(r, i < 20 ? Math.max(100, frameMs) : frameMs); });
            }
            // The encoder holds the last two frames back until later frames push
            // them out — stop() drops them, however long we wait (90 → frames
            // 0..87). Two redraws of the last picture carry them out; a redraw
            // that comes out as well is cut by the retime (blob.frameCount).
            // requestFrame() without a redraw adds nothing: the canvas is clean.
            for (var k = 0; k < 2; k++) {
                ctx.clearRect(0, 0, W, H);
                ctx.drawImage(frames[frames.length - 1], 0, 0, W, H);
                track.requestFrame();
                await new Promise(function (r) { setTimeout(r, frameMs); });
            }
            await new Promise(function (r) { setTimeout(r, Math.max(60, frameMs)); });
            rec.stop(); await stopped;
            try { track.stop(); } catch (_) {}
            if (onProgress) { try { onProgress(1); } catch (_) {} }
            var blob = new Blob(chunks, { type: 'video/webm' });
            if (!blob.size) throw new Error('alpha-recorder: empty output');
            blob.frameCount = frames.length;
            return blob;
        }

        function cleanup() {
            for (var i = 0; i < frames.length; i++) { try { frames[i].close(); } catch (_) {} }
            frames.length = 0; pending.length = 0;
        }

        return { addFrame: addFrame, finalize: finalize, cleanup: cleanup, frameCount: function () { return frames.length; } };
    }

    root.AlphaWebMRecorder = { isSupported: isSupported, createSession: createSession, _pickMime: pickMime };
})(typeof window !== 'undefined' ? window : globalThis);
